import { clamp, mixHex, rand, shade } from "./color";

const LEAF_COLORS = [
  ["#2f6b3a", "#b7e09a"], ["#3f8a45", "#f2f7c0"], ["#56a04e", "#ffe7a8"],
  ["#d9729c", "#ffd6e6"], ["#e58fb0", "#fff3f8"], ["#2a5a35", "#f6a2c4"], ["#7aa84a", "#ffd0e2"],
];

function leafSprite([base, dot]) {
  const c = document.createElement("canvas");
  c.width = 290;
  c.height = 160;
  const g = c.getContext("2d");
  g.translate(145, 80);
  g.shadowColor = "rgba(0,0,0,0.55)";
  g.shadowBlur = 10;
  g.shadowOffsetY = 4;
  g.beginPath();
  g.moveTo(-124, 0);
  g.quadraticCurveTo(-30, -66, 124, 0);
  g.quadraticCurveTo(-30, 66, -124, 0);
  const grd = g.createLinearGradient(0, -60, 0, 60);
  grd.addColorStop(0, shade(base, 30));
  grd.addColorStop(1, shade(base, -30));
  g.fillStyle = grd;
  g.fill();
  g.shadowColor = "transparent";
  g.save();
  g.clip();
  for (let i = 0; i < 26; i++) {
    g.fillStyle = Math.random() < 0.7 ? dot : shade(base, -45);
    g.globalAlpha = rand(0.35, 0.95);
    g.beginPath();
    g.ellipse(rand(-110, 110), rand(-40, 40), rand(2, 7), rand(2, 5), rand(0, 3), 0, Math.PI * 2);
    g.fill();
  }
  g.globalAlpha = 0.3;
  g.strokeStyle = "#000";
  g.lineWidth = 3;
  g.beginPath();
  g.moveTo(-118, 0);
  g.quadraticCurveTo(0, -6, 118, 0);
  g.stroke();
  g.restore();
  return c;
}

export function createWoodsScene(canvas, { onProgress, onCleared, onRustle }) {
  const ctx = canvas.getContext("2d");
  const sprites = LEAF_COLORS.map(leafSprite);
  let W = 0, H = 0, dpr = 1, L0 = 150, raf = 0, last = performance.now(), t = 0;
  let trunks = [], leaves = [], motes = [], total = 0, gone = 0;
  let cleared = false, homeOn = false, home = 0, speed = 0.75, assist = 0;
  let bg, vignette;
  const ptr = { x: 0, y: 0, t: 0, has: false };

  const trunk = (far) => ({
    x: (Math.random() < 0.5 ? -1 : 1) * rand(0.55, 5.5),
    z: far ? rand(16, 21) : rand(1, 21),
    w: rand(0.26, 0.55),
  });
  const leaf = (x, y) => {
    const s = (rand(0.8, 1.3) * L0) / 250;
    return {
      x, y, vx: 0, vy: 0, rot: rand(-Math.PI, Math.PI), vr: 0, s0: s, s,
      sp: sprites[Math.floor(Math.random() * sprites.length)], free: false, gone: false, ph: rand(0, 6.28),
    };
  };

  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = window.innerWidth;
    H = window.innerHeight;
    canvas.width = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
    L0 = (200 * Math.min(W, H)) / 800 + 50;
    const cx = W / 2, cy = H * 0.5, R = Math.max(W, H) * 0.85;
    bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
    [[0, "#fff6c8"], [0.06, "#ffe08a"], [0.18, "#f8a8cf"], [0.38, "#c9a8f0"], [0.7, "#9ec9ef"], [1, "#7fa6d6"]].forEach(([o, c]) => bg.addColorStop(o, c));
    vignette = ctx.createRadialGradient(cx, cy, Math.min(W, H) * 0.3, cx, cy, R * 0.75);
    vignette.addColorStop(0, "rgba(0,0,0,0)");
    vignette.addColorStop(1, "rgba(70,50,110,0.3)");
  }

  function init() {
    resize();
    trunks = Array.from({ length: 44 }, () => trunk(false));
    motes = Array.from({ length: 60 }, () => ({ x: rand(0, W), y: rand(0, H), vx: rand(-6, 6), vy: -rand(6, 20), r: rand(0.8, 2.6), ph: rand(0, 6) }));
    const sx = L0 * 0.62, sy = L0 * 0.36;
    for (let y = -sy * 0.3; y < H + sy * 0.5; y += sy)
      for (let x = -sx * 0.2; x < W + sx * 0.5; x += sx) leaves.push(leaf(x + rand(-0.4, 0.4) * sx, y + rand(-0.4, 0.4) * sy));
    for (let i = 0; i < leaves.length * 0.25; i++) leaves.push(leaf(rand(0, W), rand(0, H)));
    total = leaves.length;
  }

  function push(x, y, vx, vy, radius, strength) {
    let n = 0;
    for (const l of leaves) {
      if (l.gone) continue;
      const dx = l.x - x, dy = l.y - y, d = Math.hypot(dx, dy);
      if (d > radius) continue;
      const k = 1 - d / radius, nx = dx / (d || 1), ny = dy / (d || 1);
      l.vx += (vx * 0.85 + nx * strength) * k;
      l.vy += (vy * 0.85 + ny * strength) * k - 80 * k;
      l.vr += rand(-9, 9) * k;
      if (!l.free) {
        l.free = true;
        n++;
      }
    }
    if (n) onRustle(Math.min(1, n / 5));
  }

  function updateLeaves(dt) {
    for (const l of leaves) {
      if (l.gone || !l.free) continue;
      const drag = Math.exp(-0.9 * dt);
      l.vy += 420 * dt;
      l.vx = l.vx * drag + Math.sin(t * 4 + l.ph) * 140 * dt;
      l.vy *= drag;
      l.x += l.vx * dt;
      l.y += l.vy * dt;
      l.rot += l.vr * dt;
      l.s = Math.min(l.s * (1 + 0.3 * dt), l.s0 * 1.6);
      const m = L0 * 1.6;
      if (l.x < -m || l.x > W + m || l.y > H + m || l.y < -m * 3) {
        l.gone = true;
        gone++;
        onProgress(gone / total);
        if (!cleared && gone / total >= 0.7) {
          cleared = true;
          onCleared();
        }
      }
    }
    if (t > 28 && !cleared) {
      assist += dt;
      while (assist > 0.18) {
        assist -= 0.18;
        const l = leaves.find((q) => !q.free);
        if (l) {
          l.free = true;
          l.vx = (l.x < W / 2 ? -1 : 1) * rand(500, 900);
          l.vy = -rand(100, 300);
          l.vr = rand(-6, 6);
        }
      }
    }
  }

  function drawTrunks(f, horizon, near) {
    for (const tr of trunks) {
      if (near ? tr.z > 9 : tr.z <= 9) continue;
      const sx = W / 2 + (tr.x / tr.z) * f;
      const tw = (tr.w / tr.z) * f;
      const gy = horizon + (1.6 / tr.z) * f * 0.35;
      const ft = clamp((tr.z - 1) / 18);
      ctx.globalAlpha = clamp((21 - tr.z) / 3);
      ctx.fillStyle = mixHex("#3a2c3f", "#d4bdf0", Math.pow(ft, 0.7));
      ctx.fillRect(sx - tw / 2, -10, tw, gy + 10);
      ctx.beginPath();
      ctx.moveTo(sx - tw * 0.9, gy);
      ctx.lineTo(sx, gy - tw * 1.4);
      ctx.lineTo(sx + tw * 0.9, gy);
      ctx.fill();
      ctx.fillStyle = `rgba(255,236,214,${(1 - ft) * 0.35})`;
      const rim = tr.x < 0 ? sx + tw / 2 - tw * 0.16 : sx - tw / 2;
      ctx.fillRect(rim, -10, tw * 0.16, gy + 10);
    }
    ctx.globalAlpha = 1;
  }

  function drawHome(horizon) {
    const a = clamp(home / 2.5);
    if (a <= 0) return;
    const e = a * a * (3 - 2 * a);
    const hw = Math.min(W, H) * (0.12 + home * 0.006);
    const hx = W / 2, hy = horizon + hw * 0.08;
    ctx.globalCompositeOperation = "lighter";
    const glow = ctx.createRadialGradient(hx, hy - hw * 0.3, 0, hx, hy - hw * 0.3, Math.max(1, hw * 3.2));
    glow.addColorStop(0, `rgba(255,214,140,${0.75 * e})`);
    glow.addColorStop(0.4, `rgba(255,160,120,${0.25 * e})`);
    glow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = e;
    const bh = hw * 0.6;
    ctx.fillStyle = "#4a3655";
    ctx.fillRect(hx - hw / 2, hy - bh, hw, bh);
    ctx.fillRect(hx + hw * 0.2, hy - bh - hw * 0.42, hw * 0.1, hw * 0.25);
    ctx.fillStyle = "#3a2846";
    ctx.beginPath();
    ctx.moveTo(hx - hw * 0.62, hy - bh);
    ctx.lineTo(hx, hy - bh - hw * 0.45);
    ctx.lineTo(hx + hw * 0.62, hy - bh);
    ctx.fill();
    ctx.shadowColor = "#ffb347";
    ctx.shadowBlur = hw * 0.35;
    ctx.fillStyle = "#ffd27a";
    const ws = hw * 0.15;
    ctx.fillRect(hx - hw * 0.32, hy - bh * 0.72, ws, ws);
    ctx.fillRect(hx + hw * 0.17, hy - bh * 0.72, ws, ws);
    ctx.fillStyle = "#f2a65a";
    ctx.fillRect(hx - hw * 0.07, hy - bh * 0.5, hw * 0.14, bh * 0.5);
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
  }

  function frame(now) {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    t += dt;
    if (homeOn) home += dt;
    speed += ((homeOn ? 0.12 : 0.75) - speed) * (1 - Math.exp(-1.2 * dt));
    for (const tr of trunks) {
      tr.z -= speed * dt;
      if (tr.z < 0.35) Object.assign(tr, trunk(true));
    }
    trunks.sort((a, b) => b.z - a.z);
    updateLeaves(dt);

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);
    const horizon = H * 0.56, f = Math.min(W, H) * 0.9;
    ctx.globalCompositeOperation = "lighter";
    const pulse = 0.18 + Math.sin(t * 0.8) * 0.06 + clamp(home / 3) * 0.35;
    const bloom = ctx.createRadialGradient(W / 2, H * 0.5, 0, W / 2, H * 0.5, Math.min(W, H) * 0.45);
    bloom.addColorStop(0, `rgba(255,220,170,${pulse})`);
    bloom.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = bloom;
    ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = "source-over";
    const ground = ctx.createLinearGradient(0, horizon, 0, H);
    ground.addColorStop(0, "rgba(220,205,235,0.3)");
    ground.addColorStop(0.25, "#b9a3dc");
    ground.addColorStop(1, "#8a74b8");
    ctx.fillStyle = ground;
    ctx.fillRect(0, horizon, W, H - horizon);

    drawTrunks(f, horizon, false);
    drawHome(horizon);
    drawTrunks(f, horizon, true);

    for (const m of motes) {
      m.x += (m.vx + Math.sin(t + m.ph) * 8) * dt;
      m.y += m.vy * dt;
      if (m.y < -10) Object.assign(m, { y: H + 10, x: rand(0, W) });
      ctx.fillStyle = `rgba(255,255,255,${0.35 + 0.4 * Math.sin(t * 2 + m.ph) ** 2})`;
      ctx.beginPath();
      ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = "source-over";

    for (const l of leaves) {
      if (l.gone) continue;
      const r = l.rot + (l.free ? 0 : Math.sin(t * 1.3 + l.ph) * 0.05);
      const c = Math.cos(r) * l.s * dpr, s = Math.sin(r) * l.s * dpr;
      ctx.setTransform(c, s, -s, c, l.x * dpr, l.y * dpr);
      ctx.drawImage(l.sp, -145, -80);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, W, H);
    raf = requestAnimationFrame(frame);
  }

  init();
  window.addEventListener("resize", resize);
  raf = requestAnimationFrame(frame);

  return {
    pointerDown(x, y) {
      Object.assign(ptr, { x, y, t: performance.now(), has: true });
      push(x, y, 0, 0, L0 * 0.8, 700);
    },
    pointerMove(x, y, pressed) {
      const now = performance.now();
      const dt = Math.max(0.008, (now - ptr.t) / 1000);
      const vx = clamp((x - ptr.x) / dt, -3500, 3500);
      const vy = clamp((y - ptr.y) / dt, -3500, 3500);
      if (ptr.has && (pressed || Math.hypot(vx, vy) > 300)) push(x, y, pressed ? vx : vx * 0.6, pressed ? vy : vy * 0.6, L0 * (pressed ? 0.7 : 0.5), pressed ? 260 : 160);
      Object.assign(ptr, { x, y, t: now, has: true });
    },
    goHome(withHouse = true) {
      homeOn = withHouse;
      for (const l of leaves) {
        if (l.free) continue;
        const dx = l.x - W / 2, dy = l.y - H / 2, d = Math.hypot(dx, dy) || 1;
        l.free = true;
        l.vx = (dx / d) * rand(600, 1100);
        l.vy = (dy / d) * rand(600, 1100) - 200;
        l.vr = rand(-6, 6);
      }
    },
    destroy() {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    },
  };
}
